import React, { useCallback, useEffect, useLayoutEffect, useState } from "react";
import "./WalletTransactions.scss";
import { useDispatch } from "react-redux";
import CommonTable from "../../../components/common/CommonTable/CommonTable.jsx";
import NoRecord from "../../../components/common/NoRecord/NoRecord.jsx";
import CommonSearch from "../../../components/ui/CommonSearch/CommonSearch.jsx";
import ReactPagination from "../../../components/common/pagination";
import { CopyIcon } from "../../../assets/Svg/SvgImages.jsx";
import { ATS } from "../../../features/ats/atsSlice";
import { copyToClipboard } from "../../../helpers/commonHelper";
import { normalizeTransactionHistoryResponse, TRANSACTION_HISTORY_PAGE_SIZE } from "../chartDataUtils";
const TABS = {
  DEPOSIT: "deposit",
  WITHDRAW: "withdraw"
};
const TX_ID_FILTER_MAX_LENGTH = 60;
const COIN_FILTER_MAX_LENGTH = 20;
const DEPOSIT_TABLE_FIELDS = ["Tx ID", "Coin", "Amount", "From", "To", "Status", "Date"];
const WITHDRAW_TABLE_FIELDS = ["Tx ID", "Coin", "Amount", "Fee", "From", "To", "Status", "Date"];
const resolveRowFromAddress = (row, isDeposit) => {
  if (isDeposit) {
    return row?.address_from ?? row?.from_address ?? "";
  }
  return row?.from_address ?? row?.address_from ?? "";
};
const resolveRowToAddress = (row, isDeposit) => {
  if (isDeposit) {
    return row?.address_to ?? row?.to_address ?? "";
  }
  return row?.to_address ?? row?.address_to ?? "";
};
const formatTxId = txId => {
  if (!txId || typeof txId !== "string") return "-";
  const s = txId.trim();
  if (s.length <= 16) return s;
  return `${s.slice(0, 8)}...${s.slice(-8)}`;
};
const formatAddress = address => {
  if (!address || typeof address !== "string") return "-";
  const s = address.trim();
  if (s.length <= 14) return s;
  return `${s.slice(0, 6)}...${s.slice(-4)}`;
};
const SHIMMER_ROW_COUNT = 6;
const TransactionTableShimmer = ({
  columnCount
}) => Array.from({
  length: SHIMMER_ROW_COUNT
}, (_, rowIndex) => <tr key={`shimmer-${rowIndex}`} className="walletTransactions__shimmerRow">
      {Array.from({
    length: columnCount
  }, (_, colIndex) => <td key={colIndex}>
          <span className="walletTransactions__shimmerBar" style={{
      width: colIndex === 0 ? "70%" : "85%"
    }} />
        </td>)}
    </tr>);
const formatHistoryDate = iso => {
  if (!iso) return "-";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
};
const WalletTransactions = () => {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState(TABS.DEPOSIT);
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [immediateTxId, setImmediateTxId] = useState("");
  const [debouncedTxId, setDebouncedTxId] = useState("");
  const [immediateCoin, setImmediateCoin] = useState("");
  const [debouncedCoin, setDebouncedCoin] = useState("");
  useEffect(() => {
    const id = setTimeout(() => setDebouncedTxId(immediateTxId.trim()), 400);
    return () => clearTimeout(id);
  }, [immediateTxId]);
  useEffect(() => {
    const id = setTimeout(() => setDebouncedCoin(immediateCoin.trim().toLowerCase()), 400);
    return () => clearTimeout(id);
  }, [immediateCoin]);
  useLayoutEffect(() => {
    setPage(1);
    setRows([]);
    setTotalCount(0);
    setLoading(true);
  }, [debouncedTxId, debouncedCoin, activeTab]);
  const loadHistory = useCallback(async () => {
    const requestTab = activeTab;
    setLoading(true);
    setRows([]);
    const params = {
      page,
      limit: TRANSACTION_HISTORY_PAGE_SIZE
    };
    if (debouncedTxId) {
      params.txId = debouncedTxId;
    }
    if (debouncedCoin) {
      params.coin = debouncedCoin;
    }
    try {
      const fetcher = activeTab === TABS.WITHDRAW ? ATS.fetchWithdrawHistory : ATS.fetchDepositHistory;
      const result = await dispatch(fetcher(params, {
        skipGlobalLoader: true
      }));
      if (requestTab !== activeTab) return;
      if (Number(result?.status) === 200) {
        const normalized = normalizeTransactionHistoryResponse(result);
        setRows(normalized.rows);
        setTotalCount(normalized.totalCount);
      } else {
        setRows([]);
        setTotalCount(0);
      }
    } finally {
      if (requestTab === activeTab) {
        setLoading(false);
      }
    }
  }, [dispatch, activeTab, page, debouncedTxId, debouncedCoin]);
  useEffect(() => {
    loadHistory();
  }, [loadHistory]);
  const handleTabChange = tab => {
    if (tab === activeTab) return;
    setRows([]);
    setTotalCount(0);
    setLoading(true);
    setImmediateTxId("");
    setDebouncedTxId("");
    setImmediateCoin("");
    setDebouncedCoin("");
    setActiveTab(tab);
    setPage(1);
  };
  const isDepositTab = activeTab === TABS.DEPOSIT;
  const tableFields = isDepositTab ? DEPOSIT_TABLE_FIELDS : WITHDRAW_TABLE_FIELDS;
  const emptyRecordLabel = isDepositTab ? "Deposit Transactions" : "Withdraw Transactions";
  return <div className="walletTransactions commonCardbox">
      <div className="walletTransactions__header d-sm-flex align-items-center justify-content-between">
        <div className="dashHeading">
          <h3>Transactions</h3>
        </div>
        <div className="walletTransactions__filters mt-sm-0">
          <CommonSearch className="walletSearch walletTransactions__search" placeholder="Filter by coin" maxLength={COIN_FILTER_MAX_LENGTH} value={immediateCoin} loading={loading && Boolean(debouncedCoin)} onClear={() => setImmediateCoin("")} onChange={e => setImmediateCoin(e.target.value)} />
          <CommonSearch className="walletSearch walletTransactions__search" placeholder="Filter by Tx ID" maxLength={TX_ID_FILTER_MAX_LENGTH} value={immediateTxId} loading={loading && Boolean(debouncedTxId)} onClear={() => setImmediateTxId("")} onChange={e => setImmediateTxId(e.target.value)} />
        </div>
      </div>

      <ul className="walletTransactions__tabs">
        <li>
          <button type="button" className={activeTab === TABS.DEPOSIT ? "active" : ""} onClick={() => handleTabChange(TABS.DEPOSIT)}>
            Deposit
          </button>
        </li>
        <li>
          <button type="button" className={activeTab === TABS.WITHDRAW ? "active" : ""} onClick={() => handleTabChange(TABS.WITHDRAW)}>
            Withdraw
          </button>
        </li>
      </ul>

      <div className="walletTransactions__tableWrap">
        <CommonTable className={`innerCommontable ${loading ? "walletTransactions__table--shimmer" : ""}`} fields={tableFields} noRecordFound={<NoRecord text={emptyRecordLabel} />}>
          {loading ? [...TransactionTableShimmer({
          columnCount: tableFields.length
        })] : rows.length > 0 ? rows.map(row => {
          const txId = row?.tx_id ?? row?.txId ?? "";
          const rowKey = row?.id ?? txId;
          const status = String(row?.status ?? "").toLowerCase();
          const fromAddress = resolveRowFromAddress(row, isDepositTab);
          const toAddress = resolveRowToAddress(row, isDepositTab);
          return <tr key={rowKey}>
                    <td>
                      <span className="walletTransactions__txCell">
                        <span title={txId}>{formatTxId(txId)}</span>
                        {txId ? <span className="copyIcon ms-2" role="button" tabIndex={0} onClick={() => copyToClipboard(txId)} onKeyDown={e => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    copyToClipboard(txId);
                  }
                }}>
                            <CopyIcon />
                          </span> : null}
                      </span>
                    </td>
                    <td>{row?.coin_name ?? row?.coin ?? "-"}</td>
                    <td>{row?.amount ?? "-"}</td>
                    {!isDepositTab ? <td>{row?.fee ?? "-"}</td> : null}
                    <td>
                      <span title={fromAddress}>
                        {formatAddress(fromAddress)}
                      </span>
                    </td>
                    <td>
                      <span title={toAddress}>
                        {formatAddress(toAddress)}
                      </span>
                    </td>
                    <td>
                      <div className={`walletTransactions__status walletTransactions__status--${status || "unknown"}`}>
                        {row?.status ?? "-"}
                      </div>
                    </td>
                    <td>{formatHistoryDate(row?.created_at)}</td>
                  </tr>;
        }) : null}
        </CommonTable>
      </div>

      {!loading && totalCount > TRANSACTION_HISTORY_PAGE_SIZE && <div className="text-end w-100 mt-3">
          <ReactPagination handlePageChange={({
        selected
      }) => setPage(selected + 1)} activityList={totalCount} limit={TRANSACTION_HISTORY_PAGE_SIZE} forcePage={page - 1} />
        </div>}
    </div>;
};
export default WalletTransactions;